/**
 * WebSocket service for real-time push notifications.
 *
 * Connects to the WebSocket API Gateway and dispatches events
 * to registered listeners. Auto-reconnects with exponential backoff.
 *
 * Usage:
 *   wsService.connect(token);
 *   const unsub = wsService.on('position_update', (data) => { ... });
 *   // later:
 *   unsub();
 *   wsService.disconnect();
 */

import { WS_URL } from '../config';

export type WsEventType =
  | 'position_update'
  | 'notification'
  | 'action_update'
  | 'order_fill'
  | 'circuit_breaker'
  | 'bot_update'
  | 'pong';

export interface WsMessage {
  type: WsEventType;
  data: any;
}

type WsListener = (data: any) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private listeners: Map<string, Set<WsListener>> = new Map();
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private token: string = '';
  private reconnectAttempts = 0;
  private maxReconnectDelay = 30000; // 30s max
  private intentionalClose = false;

  /** True when the WebSocket is connected and ready. */
  get connected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  /**
   * Connect to the WebSocket API.
   * @param token - Cognito JWT access token
   */
  connect(token: string): void {
    if (!WS_URL) return; // WebSocket not configured
    if (this.ws && this.ws.readyState <= WebSocket.OPEN) return; // already connected

    this.token = token;
    this.intentionalClose = false;

    try {
      this.ws = new WebSocket(`${WS_URL}?token=${encodeURIComponent(token)}`);

      this.ws.onopen = () => {
        this.reconnectAttempts = 0;
        this.startPing();
      };

      this.ws.onmessage = (event: MessageEvent) => {
        this.handleMessage(event);
      };

      this.ws.onclose = () => {
        this.stopPing();
        if (!this.intentionalClose) {
          this.scheduleReconnect();
        }
      };

      this.ws.onerror = () => {
        // onclose will fire after onerror, triggering reconnect
      };
    } catch {
      this.scheduleReconnect();
    }
  }

  /** Disconnect and stop reconnecting. */
  disconnect(): void {
    this.intentionalClose = true;
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.reconnectAttempts = 0;
  }

  /**
   * Subscribe to a WebSocket event type.
   * @returns Unsubscribe function.
   */
  on(eventType: string, callback: WsListener): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }
    this.listeners.get(eventType)!.add(callback);

    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  /** Dispatch a message to all listeners for its event type. */
  private handleMessage(event: MessageEvent): void {
    try {
      const msg: WsMessage = JSON.parse(event.data);
      const listeners = this.listeners.get(msg.type);
      if (listeners) {
        listeners.forEach((cb) => {
          try {
            cb(msg.data);
          } catch {
            // Don't let a bad listener crash the service
          }
        });
      }
    } catch {
      // Ignore unparseable messages
    }
  }

  /** Send a ping every 5 minutes to keep the connection alive. */
  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(
      () => {
        if (this.ws?.readyState === WebSocket.OPEN) {
          this.ws.send(JSON.stringify({ action: 'ping' }));
        }
      },
      5 * 60 * 1000,
    );
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /** Reconnect with exponential backoff. */
  private scheduleReconnect(): void {
    if (this.intentionalClose) return;

    const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), this.maxReconnectDelay);
    this.reconnectAttempts++;

    this.reconnectTimer = setTimeout(() => {
      if (this.token) {
        this.connect(this.token);
      }
    }, delay);
  }
}

/** Singleton WebSocket service instance. */
export const wsService = new WebSocketService();
